import React, { useState } from 'react';
import { Button, Search } from 'semantic-ui-react';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { Modal } from 'src/components/custom';
import Form from 'src/components/form';
import { Typography } from 'src/components/coreUI/index';
import { useAppDispatch, useAppSelector } from 'src/store';
import { selectContentHierarchy } from 'src/store/model/class_topic';
import { useForm } from 'src/hooks';
import { createIssue } from 'src/thunks/user_issue';
import { selector } from 'src/store/model/classes';

const ReportIssue: React.FC = () => {
  return (
    <div className="p-12 w-100">
      <Modal
        size="small"
        trigger={
          <Button fluid basic color="purple">
            Report an issue
          </Button>
        }
        content={(close) => {
          return <ReportForm close={close} />;
        }}
      />
    </div>
  );
};

type FormProps = {
  close?: Function;
};

const options = [
  // {
  //   text: 'Contents Hierarchy',
  //   value: 'content_hierarchy',
  // },
  // {
  //   text: 'Sections',
  //   value: 'week_topic_id',
  // },
  {
    text: 'Modules',
    value: 'class_topic_id',
  },
  {
    text: 'Contents',
    value: 'class_topic_content_id',
  },
  {
    text: 'Others',
    value: 'others',
  },
];

const ReportForm: React.FC<FormProps> = ({ close }) => {
  const { class_id } = useParams<'class_id'>();
  const user_id = useAppSelector((state) => state.ui.app.me?.id);
  const dispatch = useAppDispatch();
  const currentClass = useAppSelector((state) =>
    selector.selectById(state, class_id!),
  );

  const {
    getFieldInfo,
    values,
    setFieldValue,
    submitForm,
    isSubmitting,
    errors,
  } = useForm<Partial<Model.UserIssue>>({
    initialValues: {
      user_id,
      type: 'class_topic_id',
      title: '',
      description: '',
      attachments: [],
      type_id: undefined,
    },
    async onSubmit(value) {
      const typeId =
        value.type === 'class_topic_content_id'
          ? value.type_id?.split('/')[1]
          : value.type_id;

      const submitValue = {
        ...value,
        type_id: typeId,
        extra_data: {
          class: {
            id: currentClass?.id,
            name: currentClass?.name,
          },
        },
      };

      await dispatch(createIssue(submitValue));
      close?.();
    },
    validationSchema: Yup.object().shape({
      title: Yup.string().required().max(255).label('Issue title'),
      description: Yup.string().required().label('Issue description'),
      attachments: Yup.array().required().min(1).label('Attachments'),
      type_id: Yup.string()
        .when('type', (type, schema) => {
          if (type === 'others') return;

          return schema.required();
        })
        .label('Type Id'),
    }),
  });

  const [searchValue, setSearchValue] = useState<string | undefined>('');

  const data = useAppSelector((state) =>
    selectContentHierarchy(state, {
      class_id,
      type: values.type!,
      searchValue,
    }),
  );

  return (
    <Form>
      <Typography.Title>REPORT AN ISSUE</Typography.Title>
      <Form.Dropdown
        options={options}
        selection
        label="TYPE"
        placeholder="Type of issue"
        {...getFieldInfo('type')}
        onChange={(event, data) => {
          setFieldValue('type', data.value);
          setFieldValue('type_id', undefined);
        }}
      />

      {values.type === 'others' ? null : (
        <Form.Field error={getFieldInfo('type_id').error}>
          <Search
            fluid
            placeholder="Search..."
            results={data}
            onResultSelect={(e, data) =>
              setFieldValue('type_id', data.result.id)
            }
            onSearchChange={(e, search) => {
              setSearchValue(search.value);
            }}
          />

          {getFieldInfo('type_id').error && (
            <div className="ui pointing above prompt label">
              {getFieldInfo('type_id').error}
            </div>
          )}
        </Form.Field>
      )}

      <Form.Input
        label="TITLE"
        placeholder="Issue title"
        {...getFieldInfo('title')}
      />
      <Form.TextArea
        label="DESCRIBE WHAT YOU ARE EXPERIENCING"
        placeholder="Issue description"
        {...getFieldInfo('description')}
      />
      <Form.ImageUploadField
        multiple
        error={errors.attachments}
        label="ATTACH SCREENSHOT (image only)"
        value={values.attachments || []}
        placeholder="Upload image"
        onChange={(urls) => {
          setFieldValue('attachments', [
            ...(values.attachments || []),
            ...urls,
          ]);
        }}
        onClear={(url) => {
          setFieldValue(
            'attachments',
            values?.attachments?.filter((u) => u !== url),
          );
        }}
      />

      <Button
        onClick={submitForm}
        loading={isSubmitting}
        primary
        className="d-block margin-left-auto"
      >
        Submit
      </Button>
    </Form>
  );
};

export default ReportIssue;
